import { useOrganization } from '@components/OrganizationBoundary';
import { useAuth } from '@hooks/useAuth';
import {
  updateTask,
  UpdateTaskPayload,
  deleteTask,
  DeleteTaskPayload,
  restoreTask,
  CreateTaskPayload,
  createTask,
  fetchTaskAttachment,
  fetchTaskById
} from '@redux/actions/tasks';
import { useCallback } from 'react';
import useTypedDispatch from '@hooks/useTypedDispatch';
import {
  useWebSocketDebouncedSubscription,
  useWebSocketSubscription
} from '@hooks/useWebSocketClient';
import {
  handleTaskDelete,
  handleTaskAttachmentDelete,
  handleTaskCommentCreate,
  handleTaskRestore
} from '@redux/reducers/tasks';
import store from '@redux/store';
import { taskStateSelector } from '@redux/selectors/tasks';
import { useCurrentWorkspace } from '@hooks/workspace';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_TASKS_LAST_READ } from '@hooks/queries/task';

export type CreateTaskParams = Omit<CreateTaskPayload, 'userId' | 'planType'>;

export type UpdateTaskParams = Omit<UpdateTaskPayload, 'userId' | 'planType'>;

export type DeleteTaskParams = Omit<DeleteTaskPayload, 'userId' | 'planType'>;

export function useCreateTask() {
  const dispatch = useTypedDispatch();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization(false);
  const userId = user?.id;
  const planType = currentOrganization?.entity?.planType;
  return useCallback(
    (params: CreateTaskParams) => {
      if (!userId) throw new Error('Auth required');
      if (!planType) throw new Error('Plan type required');

      return dispatch(
        createTask({
          ...params,
          userId,
          planType
        })
      );
    },
    [dispatch, userId, planType]
  );
}

export function useUpdateTask() {
  const dispatch = useTypedDispatch();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization(false);
  const userId = user?.id;
  const planType = currentOrganization?.entity?.planType;
  return useCallback(
    (params: UpdateTaskParams) => {
      if (!userId) throw new Error('Auth required');
      if (!planType) throw new Error('Plan type required');
      return dispatch(
        updateTask({
          ...params,
          userId,
          planType
        })
      );
    },
    [dispatch, userId, planType]
  );
}

export function useDeleteTask() {
  const dispatch = useTypedDispatch();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization(false);
  const userId = user?.id;
  const planType = currentOrganization?.entity?.planType;
  return useCallback(
    (params: DeleteTaskParams) => {
      if (!userId) throw new Error('Auth required');
      if (!planType) throw new Error('Plan type required');

      return dispatch(
        deleteTask({
          ...params,
          userId,
          planType
        })
      );
    },
    [dispatch, userId, planType]
  );
}

export function useRestoreTask() {
  const dispatch = useTypedDispatch();
  return useCallback(
    (params: { id: string }) => {
      return dispatch(restoreTask(params));
    },
    [dispatch]
  );
}

export function useTaskWebSocketSubscription(options?: {
  workspaceId?: string;
}) {
  const dispatch = useTypedDispatch();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const queryClient = useQueryClient();
  const workspaceId = options?.workspaceId ?? currentWorkspace?.id;

  useWebSocketSubscription(
    async ({
      causedByMe,
      payload: { taskId, workspaceId: msgWsId, reason }
    }) => {
      if (msgWsId !== workspaceId) return;
      if (causedByMe) return;
      queryClient.invalidateQueries({ queryKey: [QUERY_TASKS_LAST_READ] });
      const state = taskStateSelector(store.getState(), { taskId });
      if (!state.entity) {
        dispatch(fetchTaskById({ id: taskId, fetchType: 'initial' }));
      } else if (reason === 'restored-from-recycle-bin') {
        dispatch(handleTaskRestore({ taskId }));
      }
    },
    'task-created'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.taskId,
    async (messages) => {
      const {
        payload: { taskId }
      } = messages[0];
      const state = taskStateSelector(store.getState(), { taskId });
      if (!state.entity) return;
      dispatch(fetchTaskById({ id: taskId, fetchType: 'refresh' }));
    },
    1000,
    ({ causedByMe, payload: { workspaceId: msgWsId } }) => {
      if (causedByMe) return false;
      if (msgWsId !== workspaceId) return false;
      return true;
    },
    'task-updated'
  );

  useWebSocketSubscription(
    async ({
      causedByMe,
      payload: { taskId, reason, workspaceId: msgWsId }
    }) => {
      if (causedByMe) return;
      if (msgWsId !== workspaceId) return;
      dispatch(
        handleTaskDelete({
          taskId,
          permanent: reason === 'deleted-permanently'
        })
      );
    },
    'task-removed'
  );

  useWebSocketSubscription(
    async ({
      payload: {
        attachmentId,
        workspaceId: msgWsId,
        target: { taskId, type }
      }
    }) => {
      if (type !== 'task-attachment') return;
      if (msgWsId !== workspaceId) return;
      const state = taskStateSelector(store.getState(), { taskId });
      if (!state.entity) return;
      if (state.entity.attachments.some((x) => x.id === attachmentId)) return;
      dispatch(
        fetchTaskAttachment({
          taskId,
          id: attachmentId
        })
      );
    },
    'attachment-created'
  );

  useWebSocketSubscription(
    async ({
      causedByMe,
      payload: {
        attachmentId,
        workspaceId: msgWsId,
        target: { taskId, type }
      }
    }) => {
      if (causedByMe) return;
      if (type !== 'task-attachment') return;
      if (msgWsId !== workspaceId) return;
      dispatch(
        handleTaskAttachmentDelete({
          taskId,
          id: attachmentId
        })
      );
    },
    'attachment-removed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.taskId,
    async (messages) => {
      const {
        payload: { taskId }
      } = messages[0];
      dispatch(handleTaskCommentCreate({ taskId }));
    },
    1000,
    ({ causedByMe, payload: { workspaceId: msgWsId } }) => {
      if (causedByMe) return false;
      if (msgWsId !== workspaceId) return false;
      return true;
    },
    'task-comment-created'
  );
}
